/**
 * Python Dependencies Service
 */
import { execSync } from 'child_process';
import { promises as fs } from 'fs';
import path from 'path';
import pLimit from 'p-limit';
import type {
    DepsServiceInterface,
    PackageInfo,
    CheckResult,
    UpgradeOptions,
    UpgradeType,
    PackageManager
} from '../types/deps.types.js';

interface PyPIPackageInfo {
    info: { version: string; };
}

export class DepsPythonService implements DepsServiceInterface {
    readonly ecosystem = 'python' as const;
    private versionCache: Map<string, string> = new Map();

    async check(cwd: string, onProgress?: (completed: number, total: number) => void): Promise<CheckResult> {
        const manager = await this.detectPackageManager(cwd);
        let packages: PackageInfo[];

        if (manager === 'pipenv') {
            packages = await this.checkPipenv(cwd, onProgress);
        } else {
            packages = await this.checkPip(cwd, onProgress);
        }

        return { ecosystem: this.ecosystem, manager, packages };
    }

    async upgrade(cwd: string, options: UpgradeOptions): Promise<void> {
        const manager = await this.detectPackageManager(cwd);
        const commands = this.getUpgradeCommands(options.packages);

        if (manager === 'pipenv') {
            if (commands.deps || commands.devDeps) {
                const allPackages = options.packages.map(p => p.name).join(' ');
                this.executeUpgrade(`pipenv update ${allPackages}`, cwd);
            }
        } else {
            if (commands.deps) this.executeUpgrade(commands.deps, cwd);
            if (commands.devDeps) this.executeUpgrade(commands.devDeps, cwd);
        }
    }

    getUpgradeCommands(packages: PackageInfo[]): { deps: string | null; devDeps: string | null } {
        const deps = packages.filter(p => p.type === 'dep');
        const devDeps = packages.filter(p => p.type === 'dev');

        let depsCmd: string | null = null;
        let devDepsCmd: string | null = null;

        if (deps.length > 0) {
            const pkgList = deps.map(p => `${p.name}==${p.latest}`).join(' ');
            depsCmd = `pip install --upgrade ${pkgList}`;
        }

        if (devDeps.length > 0) {
            const pkgList = devDeps.map(p => `${p.name}==${p.latest}`).join(' ');
            devDepsCmd = `pip install --upgrade ${pkgList}`;
        }

        return { deps: depsCmd, devDeps: devDepsCmd };
    }

    private async detectPackageManager(cwd: string): Promise<PackageManager> {
        if (await this.fileExists(cwd, 'Pipfile')) return 'pipenv';
        return 'pip';
    }

    private async checkPip(cwd: string, onProgress?: (completed: number, total: number) => void): Promise<PackageInfo[]> {
        const requirementsPath = path.join(cwd, 'requirements.txt');
        try {
            const content = await fs.readFile(requirementsPath, 'utf-8');
            const packages = this.parseRequirementsTxt(content);
            return await this.fetchBulkVersions(packages, onProgress);
        } catch (error) {
            if ((error as NodeJS.ErrnoException).code === 'ENOENT') {
                throw new Error('Không tìm thấy requirements.txt trong thư mục hiện tại');
            }
            throw new Error(`Lỗi đọc requirements.txt: ${(error as Error).message}`);
        }
    }

    private async checkPipenv(cwd: string, onProgress?: (completed: number, total: number) => void): Promise<PackageInfo[]> {
        try {
            const output = execSync('pipenv run pip list --outdated --format=json', {
                cwd, encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe']
            });

            const outdated = JSON.parse(output) as Array<{ name: string; version: string; latest_version: string; }>;
            const total = outdated.length;
            let completed = 0;

            const packages: PackageInfo[] = outdated.map(pkg => {
                completed++;
                onProgress?.(completed, total);
                return {
                    name: pkg.name,
                    current: pkg.version,
                    latest: pkg.latest_version,
                    latestWithPrefix: pkg.latest_version,
                    type: 'dep',
                    upgradeType: this.getUpgradeType(pkg.version, pkg.latest_version),
                    canUpgrade: true
                };
            });

            return packages;
        } catch (error) {
            throw new Error(`Lỗi kiểm tra pipenv packages: ${(error as Error).message}`);
        }
    }

    private parseRequirementsTxt(content: string): Array<{ name: string; current: string; type: 'dep' | 'dev' }> {
        const packages: Array<{ name: string; current: string; type: 'dep' | 'dev' }> = [];
        const lines = content.split('\n');

        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || trimmed.startsWith('#')) continue;

            const match = trimmed.match(/^([a-zA-Z0-9_-]+)\s*([=><]=?)\s*([0-9.]+)/);
            if (match) {
                const [, name, , version] = match;
                packages.push({ name: name.toLowerCase(), current: version, type: 'dep' });
            }
        }

        return packages;
    }

    private async fetchBulkVersions(
        packages: Array<{ name: string; current: string; type: 'dep' | 'dev' }>,
        onProgress?: (completed: number, total: number) => void
    ): Promise<PackageInfo[]> {
        const limit = pLimit(10);
        const total = packages.length;
        let completed = 0;

        const results = await Promise.all(
            packages.map(pkg => limit(async () => {
                const latest = await this.fetchLatestVersion(pkg.name);
                completed++;
                onProgress?.(completed, total);

                if (!latest) return null;
                const canUpgrade = this.isNewerVersion(latest, pkg.current);
                if (!canUpgrade) return null;

                return {
                    name: pkg.name,
                    current: pkg.current,
                    latest,
                    latestWithPrefix: latest,
                    type: pkg.type,
                    upgradeType: this.getUpgradeType(pkg.current, latest),
                    canUpgrade: true
                } satisfies PackageInfo;
            }))
        );

        return results.filter((r): r is PackageInfo => r !== null);
    }

    private async fetchLatestVersion(packageName: string): Promise<string | null> {
        if (this.versionCache.has(packageName)) return this.versionCache.get(packageName)!;

        try {
            const response = await fetch(
                `https://pypi.org/pypi/${encodeURIComponent(packageName)}/json`,
                { signal: AbortSignal.timeout(10000) }
            );

            if (!response.ok) return null;
            const data = await response.json() as PyPIPackageInfo;
            this.versionCache.set(packageName, data.info.version);
            return data.info.version;
        } catch {
            return null;
        }
    }

    private getUpgradeType(current: string, latest: string): UpgradeType {
        const currentParts = current.split('.').map(Number);
        const latestParts = latest.split('.').map(Number);
        if ((latestParts[0] || 0) > (currentParts[0] || 0)) return 'major';
        if ((latestParts[1] || 0) > (currentParts[1] || 0)) return 'minor';
        return 'patch';
    }

    private isNewerVersion(remote: string, local: string): boolean {
        const remoteParts = remote.split('.').map(Number);
        const localParts = local.split('.').map(Number);

        for (let i = 0; i < 3; i++) {
            const r = remoteParts[i] || 0;
            const l = localParts[i] || 0;
            if (r > l) return true;
            if (r < l) return false;
        }

        return false;
    }

    private async fileExists(cwd: string, filename: string): Promise<boolean> {
        try {
            await fs.access(path.join(cwd, filename));
            return true;
        } catch {
            return false;
        }
    }

    private executeUpgrade(command: string, cwd: string): void {
        execSync(command, { cwd, stdio: 'inherit', env: { ...process.env, FORCE_COLOR: '1' } });
    }
}
